import {
  LaunchStatusEvent,
  LaunchStatusType,
  ModelDefinition,
  PlatformNext,
  UndefinedModelDefinition,
  InputEmitter,
  DefaultStreamerOptions,
  StreamerStatus,
} from "@pureweb/platform-sdk";

import {
  useStreamer,
  useLaunchRequest,
  IdleTimeout,
  LaunchRequestOptions,
  VideoStream,
  System,
} from "@pureweb/platform-sdk-react";

import * as qs from "query-string";
import React, { useEffect, useState, useRef } from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { Button, Icon } from "semantic-ui-react";
import useAsyncEffect from "use-async-effect";
import { ethers } from "ethers";

import "./styles.css";
import clientConfig from "../../client";

// const [streamerStatus, emitter, videoStream, audioStream, messageSubject] = useStreamer(signallingConnection, streamerOptions);
// https://opensea.io/assets/ethereum/0x219b2276a95ded887d1281c0841af4161c4bb415/99
// https://opensea.io/blog/announcements/announcing-embeddable-nfts/

import { LaunchView } from "../../Launch";
import logger from "../../Log";

import EmbeddedView from "../../components/EmbeddedView";
import useWindowSize from "../../hooks/useWindowSize";

enum BabyJungleEvents {
  JoinDigitalJungle = "JoinDigitalJungle",
  OpenDansStore = "OpenDansStore",
  OpenMerchStore = "OpenMerchStore",
}

const client: ClientJson = clientConfig as ClientJson;

class ClientJson {
  environmentId?: string;
  launchType?: string;
  projectId?: string;
  modelId?: string;
  version?: string;
  endpoint?: string;
  usePointerLock?: boolean;
  pointerLockRelease?: boolean;
  useNativeTouchEvents?: boolean;
}

class ClientOptions {
  // Overridable connection options
  LaunchType?: string;

  // Launch queue configuration
  ProjectId?: string;
  ModelId?: string;
  Version?: string;
  EnvironmentId?: string;
  Endpoint?: string;

  // Overridable streamer options
  ForceRelay = false;
  UseNativeTouchEvents?: boolean;
  UsePointerLock?: boolean;
  PointerLockRelease?: boolean;

  isValid(): boolean {
    if (!this.ProjectId) {
      return false;
    }
    if (!this.ModelId) {
      return false;
    }
    return true;
  }
}

// Initialize audio.
// load() must be called from a user interaction, especially to retain iOS audio
// this can be 'mouseup', 'touchend' or 'keypress'
// Pass the audioStream created from useStreamer as the srcObject to play game audio.
const audio = new Audio();
audio.autoplay = true;
audio.volume = 0.5;

// Parse query parameters
const query = qs.parse(window.location.search);
const clientOptions: ClientOptions = new ClientOptions();
clientOptions.LaunchType = (query["launchType"] as string) ?? client.launchType;
if (query["collaboration"] && query["collaboration"] == "true")
  clientOptions.LaunchType = "local";

clientOptions.Endpoint = (query["endpoint"] as string) ?? client.endpoint;
clientOptions.ProjectId = (query["projectId"] as string) ?? client.projectId;
clientOptions.ModelId = (query["modelId"] as string) ?? client.modelId;
clientOptions.Version = (query["version"] as string) ?? client.version;
clientOptions.EnvironmentId =
  (query["environmentId"] as string) ?? client.environmentId;
// use client json config if usePointerLock query string parameter is undefined, else use query string parameter. Default to false if non are present
clientOptions.UsePointerLock =
  (query["usePointerLock"] === undefined
    ? client.usePointerLock
    : query["usePointerLock"] === "true") ?? true;
// release the pointer lock on mouse up if true
clientOptions.PointerLockRelease =
  (query["pointerLockRelease"] === undefined
    ? client.pointerLockRelease
    : query["pointerLockRelease"] === "true") ?? true;

clientOptions.ForceRelay = query["forceRelay"] !== undefined ?? false;

clientOptions.UseNativeTouchEvents =
  (query["useNativeTouchEvents"] === undefined
    ? client.useNativeTouchEvents
    : query["useNativeTouchEvents"] === "true") ?? false;
// Initialize platform reference
const platform = new PlatformNext();
platform.initialize({
  endpoint: clientOptions.Endpoint || "https://api.pureweb.io",
});

const App: React.FC<{ address: string }> = ({ address }) => {
  const [modelDefinitionUnavailable, setModelDefinitionUnavailable] =
    useState(false);
  const [modelDefinition, setModelDefinition] = useState(
    new UndefinedModelDefinition()
  );
  const [availableModels, setAvailableModels] = useState<ModelDefinition[]>();
  const [launchRequestError, setLaunchRequestError] = useState<Error>();
  const streamerOptions = DefaultStreamerOptions;
  const [isOpenseaEmbed, setOpeanseEmbed] = useState(false);

  useAsyncEffect(async () => {
    if (clientOptions.ProjectId) {
      logger.info(
        "xxx Initializing available models: " + clientOptions.ProjectId
      );
      try {
        await platform.useAnonymousCredentials(
          clientOptions.ProjectId,
          clientOptions.EnvironmentId
        );
        await platform.connect();
        logger.info("Agent Connected: " + platform.agent.id);
        streamerOptions.iceServers = platform.agent.serviceCredentials
          .iceServers as RTCIceServer[];
        streamerOptions.forceRelay = clientOptions.ForceRelay;
        const models = await platform.getModels();
        setAvailableModels(models);
        logger.debug("Available models", models);
      } catch (err) {
        logger.error(err);
        logger.error("xxx e.message", e.message);
      }
    }
  }, [clientOptions]);

  useEffect(() => {
    if (availableModels?.length) {
      const selectedModels = availableModels.filter(function (
        model: ModelDefinition
      ): boolean {
        if (clientOptions.ModelId === model.id) {
          // If there is a version specified and we encounter it
          if (
            clientOptions.Version &&
            clientOptions.Version === model.version
          ) {
            return true;
          }
          // If there is no version specified and we find the primary version
          if (!clientOptions.Version && model.active) {
            return true;
          }
        }
        return false;
      });
      if (selectedModels?.length) {
        setModelDefinition(selectedModels[0]);
      } else {
        setModelDefinitionUnavailable(true);
      }
    }
  }, [availableModels]);

  const launchRequestOptions: LaunchRequestOptions = {
    regionOverride: query["regionOverride"] as string,
    virtualizationProviderOverride: query[
      "virtualizationProviderOverride"
    ] as string,
  };
  const [status, launchRequest, queueLaunchRequest] = useLaunchRequest(
    platform,
    modelDefinition,
    launchRequestOptions
  );
  const [streamerStatus, emitter, videoStream, audioStream, messageSubject] =
    useStreamer(platform, launchRequest, streamerOptions);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let screenTypeMsg: string;
    if ("ontouchstart" in document.documentElement) {
      screenTypeMsg = "SetMobile:bool:true";
      //    console.log("your device is a touch screen device.");
    } else {
      screenTypeMsg = "SetMobile:bool:false";
      //    console.log("your device is NOT a touch device");
    }

    const authMessage =
      address.length > 0
        ? "SetAuthorization:bool:true"
        : "SetAuthorization:bool:false";
    if (emitter && streamerStatus === StreamerStatus.Connected) {
      // setTimeout(() => {
      //   window.alert("testing: we are hitting mobile", screenTypeMsg);
      emitter.EmitUIInteraction(authMessage);
      emitter.EmitUIInteraction(screenTypeMsg);
      // }, 5_000);
    }
  }, [address, streamerStatus, emitter]);

  useEffect(() => {
    if (streamerStatus === StreamerStatus.Failed) {
      platform.disconnect();
    }
  }, [streamerStatus]);

  if (audioStream) {
    audio.srcObject = audioStream;
  }

  const launch = async () => {
    setLoading(true);
    audio.load();

    if (clientOptions.LaunchType !== "local") {
      try {
        await queueLaunchRequest();
      } catch (err) {
        setLaunchRequestError(err);
      }
    }
  };

  // Log status messages
  useEffect(() => {
    logger.info("Status", status, streamerStatus);
  }, [status, streamerStatus]);

  // Subscribe to game messages
  useEffect(() => {
    const subscription = messageSubject.subscribe(
      (value: string) => {
        console.log("Message details:");
        console.log(value);
        if (value === BabyJungleEvents.JoinDigitalJungle) {
          window.open("https://www.babyjunglestore.com/shop/");
        } else if (value === BabyJungleEvents.OpenDansStore) {
          window.open("https://www.instagram.com/watchpartsmotorcycles/");
        } else if (value === BabyJungleEvents.OpenMerchStore) {
          window.open("https://www.babyjunglestore.com/shop/");
        }
      },
      (err) => {
        logger.error(err);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [messageSubject]);

  const windowSize = useWindowSize();

  // console.log("yyy2 test props ", {
  //   videoStream,
  //   streamerStatus,
  //   status,
  //   emitter,
  //   clientOptions,
  // });

  // Notify user of missing or errors in configuration
  if (!clientOptions.isValid()) {
    return (
      <div
        style={{
          display: "flex",
          height: "100%",
          overflow: "none",
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <p>
          Your client has one or more configuration errors. Please consult the{" "}
          <a href="https://www.npmjs.com/package/@pureweb/cra-template-pureweb-client">
            {" "}
            README{" "}
          </a>{" "}
          for details on how to configure the client template.
        </p>
      </div>
    );
  }

  if (modelDefinitionUnavailable) {
    return (
      <div
        style={{
          display: "flex",
          height: "100%",
          overflow: "none",
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span>The model that you have requested does not exist</span>
      </div>
    );
  }

  if (launchRequestError) {
    return (
      <div
        style={{
          display: "flex",
          height: "100%",
          overflow: "none",
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span>
          {process.env.NODE_ENV === "development"
            ? `There was an error with the launch request: ${launchRequestError}`
            : "It appears the requested model is currently not online as per your set schedule. Please contact support if it should be available."}
        </span>
      </div>
    );
  }

  // Begin connection
  if (streamerStatus === StreamerStatus.Disconnected) {
    return (
      <div
        style={{
          display: "flex",
          height: "100%",
          overflow: "none",
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <h2>Disconnected from stream</h2>
      </div>
    );
  }

  if (streamerStatus === StreamerStatus.Failed) {
    return (
      <div
        style={{
          display: "flex",
          height: "100%",
          overflow: "none",
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <h2>Failure during stream</h2>
        <h2>Please refresh to request a new session</h2>
      </div>
    );
  }

  if (streamerStatus === StreamerStatus.Withdrawn) {
    return (
      <div
        style={{
          display: "flex",
          height: "100%",
          overflow: "none",
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <h2>Streamer contribution withdrawn</h2>
      </div>
    );
  }

  if (loading) {
    const isIPhone =
      System.Browser().os === "iOS" &&
      !window.navigator.userAgent.includes("iPad");

    const isLandscapeIphone =
      isIPhone && windowSize.width && windowSize.width > 600;

    return (
      <div
        style={{
          ...(isLandscapeIphone && {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }),
        }}
      >
        <EmbeddedView
          isOpenseaEmbed={isOpenseaEmbed}
          VideoStream={videoStream}
          StreamerStatus={streamerStatus as StreamerStatus}
          LaunchRequestStatus={status}
          InputEmitter={emitter}
          UseNativeTouchEvents={clientOptions.UseNativeTouchEvents!}
          UsePointerLock={clientOptions.UsePointerLock!}
          PointerLockRelease={clientOptions.PointerLockRelease!}
        />
      </div>
    );
  } else if (clientOptions.LaunchType !== "local" && !availableModels) {
    return (
      <div
        style={{
          display: "flex",
          height: "100%",
          overflow: "none",
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <h2>Initializing...</h2>
      </div>
    );
  } else if (clientOptions.LaunchType !== "local" && !availableModels?.length) {
    return (
      <div
        style={{
          display: "flex",
          height: "100%",
          overflow: "none",
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <h2>No models are currently available in this environment.</h2>
      </div>
    );
  } else {
    return (
      <>
        <LaunchView Launch={launch} />
      </>
    );
  }
};

const AppWrapper: React.FC = () => {
  type ContractInstance = {
    signer: ethers.Signer | null;
    provider: ethers.providers.Web3Provider | null;
    address: string;
  };

  const [ci, setContract] = useState<ContractInstance>({
    signer: null,
    provider: null,
    address: "",
  });

  // const [isMoonbirds, updateMbStatus] = useState(false);
  // const [connectWallet, disconnect] = useConnect({ setContract });

  // const connect = () => {
  //   try {
  //     connectWallet();
  //   } catch (e: any) {
  //     window.alert(e);
  //   }
  // };

  // const moonbirdsContractAddress = "0x23581767a106ae21c074b2276d25e5c3e136a68b";

  // if (ci.provider && ci.address) {
  //   const nftContract = new ethers.Contract(
  //     moonbirdsContractAddress,
  //     erc721,
  //     ci.provider
  //   );

  //   nftContract.balanceOf(ci.address).then((balance: any) => {
  //     const hasMoonbirds = balance._hex !== "0x00";
  //     if (isMoonbirds !== hasMoonbirds) {
  //       updateMbStatus(hasMoonbirds);
  //     }
  //   });
  // }

  return System.IsBrowserSupported() ? (
    <>
      {/* <Navbar
        onConnect={connect}
        address={ci.address}
        disconnect={disconnect}
      /> */}
      {/* Does wallet have moonbirds? : {isMoonbirds ? "Yes" : "No"} */}
      <App address={ci.address} />
    </>
  ) : (
    <div className="ui red segment center aligned basic">
      <h2 className="header">Your browser is currently unsupported</h2>
    </div>
  );
};

export default AppWrapper;
